// Modules
import { useEffect } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// CSS and assets
import '@global/App/style.scss';

import { Header } from '@global/components/Header';
import { Footer } from '@global/components/Footer';

// Utils
import { mainRoutes, protectedRoutes } from '@global/router';
import * as Constants from '@global/App/constants/constants';
import { prefixPath } from '@global/localization/services';
import { setupHtmlLang } from '@global/utils/lang';

import { useAppDispatch } from '@global/store/hooks';
import { useAuth } from '@global/hooks/useAuth';
import { setUser } from '@global/store/slices/user/userSlice';
import { setLanguage } from '@global/store/slices/config/configSlice';
import { LOCALE_CODES } from '@global/types/types';
import RouteWrapper from '@global/App/RouteWrapper';
import { getCountries } from '@global/store/slices/country/extraReducers/getCountries';
import { SiteConfig } from '../utils/SiteConfig';

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();

  const language = i18n.language as LOCALE_CODES;
  const { user, isLoggedIn } = useAuth();

  const redirectPath = prefixPath(
    Constants.Pages.SUBSCRIPTION_DETAILS,
    i18n.language
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setUser(user));
  }, [user]);

  useEffect(() => {
    dispatch(setLanguage(language));
    setupHtmlLang(language);
  }, [language]);

  useEffect(() => {
    dispatch(getCountries());
    setupHtmlLang(language);
  }, []);

  return (
    <>
      <Header />
      <main className="app">
        <div className="page-wrapper">
          <Switch>
            {mainRoutes(SiteConfig.isNonLocalizedSite()).map(
              ({ path, component: Component }) => (
                <Route exact key={path} path={path}>
                  <RouteWrapper path={path}>
                    <Component />
                  </RouteWrapper>
                </Route>
              )
            )}

            {isLoggedIn &&
              protectedRoutes(SiteConfig.isNonLocalizedSite()).map(
                ({ path, component: Component }) => (
                  <Route exact key={path} path={path}>
                    <RouteWrapper path={path}>
                      <Component />
                    </RouteWrapper>
                  </Route>
                )
              )}

            <Redirect
              to={{
                pathname: redirectPath,
                state: { from: location },
              }}
            />
          </Switch>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default App;
